import { ReactElement, useContext, useState, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { Features } from '@lessonPlanner/constants';

import GenerateButton from '../components/GenerateButton';
import GenericBanner from '../components/GenericBanner';
import MainContent from '../components/MainContent';
import AmericanST from '../components/summative-test/AmericanST';
import InternationalST from '../components/summative-test/InternationalST';
import LocalST from '../components/summative-test/LocalST';
import { PAGE_BUTTON_TEXT } from '../constants/buttonConstants';
import formDefaultValues from '../constants/forms/summative-test/default';
import FORM_CONTENT_MAPPER_KEYS from '../constants/forms/summative-test/formContentMapperKeys';
import {
  IB_SUMMATIVE_TEST_PAGE_DESCRIPTION,
  SUMMATIVE_TEST_PAGE_DESCRIPTION,
} from '../constants/summativeTest';
import { useGeneratePause } from '../context/generate/provider';
import PortalContext from '../context/portal/context';
import { PageKey, SummativeTestProperties } from '../enums/pageKey';
import { Portal } from '../enums/portal.enum';
import { useScrollToError } from '../hooks/useScrollToError';
import useTopics from '../hooks/useTopics';
import SidebarLayout from '../layouts/SidebarLayout';
import SummativeTestSchema from '../schemas/summative-test/SummativeTestSchema';
import { summativeTestProperties } from '../types/page';
import { mapFormContents } from '../utils/mapFormContents';

function getDefaultValues(portal: Portal) {
  const defaultValues = formDefaultValues();
  switch (portal) {
    case Portal.PHILIPPINES:
      return defaultValues.LocalSummativeTest;
    case Portal.INTERNATIONAL:
      return defaultValues.InternationalSummativeTest;
    case Portal.USA:
      return defaultValues.AmericanSummativeTest;
    default:
      return {};
  }
}

function getFormContentMapper(portal: Portal) {
  switch (portal) {
    case Portal.PHILIPPINES:
      return FORM_CONTENT_MAPPER_KEYS.LocalSummativeTest;
    case Portal.INTERNATIONAL:
      return FORM_CONTENT_MAPPER_KEYS.InternationalSummativeTest;
    case Portal.USA:
      return FORM_CONTENT_MAPPER_KEYS.AmericanSummativeTest;
    default:
      return [];
  }
}

function getFormFields(portal: Portal) {
  switch (portal) {
    case Portal.PHILIPPINES:
      return [
        SummativeTestProperties.topics,
        SummativeTestProperties.gradeLevel,
        SummativeTestProperties.subject,
        SummativeTestProperties.format,
        SummativeTestProperties.language,
      ];
    case Portal.INTERNATIONAL:
      return [
        SummativeTestProperties.topics,
        SummativeTestProperties.curriculum,
        SummativeTestProperties.gradeLevel,
        SummativeTestProperties.subject,
        SummativeTestProperties.language,
        SummativeTestProperties.assessmentTypes,
      ];
    case Portal.USA:
      return [
        SummativeTestProperties.topics,
        SummativeTestProperties.gradeLevel,
        SummativeTestProperties.subject,
        SummativeTestProperties.curriculumFramework,
        SummativeTestProperties.standard,
        SummativeTestProperties.strategies,
      ];
  }
}

const defaultPremiumCreditCost = parseInt(
  process.env.NEXT_PUBLIC_GENERATE_PREMIUM_SUMMATIVE_COST,
);

export default function SummativeTest(): JSX.Element {
  const { selectedPortal } = useContext(PortalContext);
  const isLocalPortal = selectedPortal === Portal.PHILIPPINES;
  const isUSAPortal = selectedPortal === Portal.USA;
  const isInternationalPortal = selectedPortal === Portal.INTERNATIONAL;
  const defaultValues = getDefaultValues(selectedPortal);
  const formContentMapper = getFormContentMapper(selectedPortal);
  const formFields = getFormFields(selectedPortal);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const summativeTestForm = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(SummativeTestSchema(selectedPortal)),
    context: {
      isLocalPortal,
      isUSAPortal,
      isInternationalPortal,
    },
  });

  const {
    formState: { errors },
    watch,
  } = summativeTestForm;

  const watchedValues = watch() as typeof defaultValues;

  const topicsList = useTopics({
    topics: Array.isArray(watchedValues.topics) ? watchedValues.topics : [],
    setValue: summativeTestForm.setValue,
    triggerValidation: summativeTestForm.trigger,
    FORM_FIELDS: formFields,
  });

  const hasErrors = Object.entries(errors).length > 0;
  useScrollToError(errors);

  const { pausedButtonText, isGeneratePaused } = useGeneratePause();
  const finalButtonText = isGeneratePaused
    ? pausedButtonText
    : PAGE_BUTTON_TEXT[PageKey.summativeTest];

  const isGenerateButtonDisabled = isLoading || hasErrors || isGeneratePaused;

  const banner = (
    <GenericBanner
      isPremium={true}
      title="Summative Test"
      content={
        isLocalPortal
          ? SUMMATIVE_TEST_PAGE_DESCRIPTION
          : IB_SUMMATIVE_TEST_PAGE_DESCRIPTION
      }
    />
  );

  const formContents = useMemo(() => {
    const mergedValues = mapFormContents<SummativeTestProperties>(
      defaultValues,
      watchedValues,
      formContentMapper,
    );

    return {
      ...mergedValues,
      feature: Features.SUMMATIVE_TEST,
    };
  }, [watchedValues, formContentMapper, defaultValues]);

  async function customFormValidator() {
    const isValid = await summativeTestForm.trigger();
    if (!isValid) {
      return;
    }

    if ('topics' in formContents) {
      const filteredTopics = formContents.topics.filter(
        (topic) => topic.topic.trim() !== '',
      );
      filteredTopics.forEach((topic) => {
        topic.subtopics = topic.subtopics.filter(
          (subtopic) => subtopic.trim() !== '',
        );
      });
    }
  }

  return (
    <MainContent
      banner={banner}
      creditCost={defaultPremiumCreditCost}
      formContents={formContents as summativeTestProperties}
      pageKey={PageKey.summativeTest}
      setIsLoading={setIsLoading}
    >
      <>
        {isLocalPortal && (
          <LocalST
            errors={errors}
            formState={summativeTestForm}
            setValue={summativeTestForm.setValue}
            topicsList={topicsList}
            triggerValidation={summativeTestForm.trigger}
            watchedValues={watchedValues}
          />
        )}

        {isInternationalPortal && (
          <InternationalST
            errors={errors}
            formState={summativeTestForm}
            setValue={summativeTestForm.setValue}
            topicsList={topicsList}
            triggerValidation={summativeTestForm.trigger}
            watchedValues={watchedValues}
          />
        )}

        {isUSAPortal && (
          <AmericanST
            errors={errors}
            formState={summativeTestForm}
            setValue={summativeTestForm.setValue}
            topicsList={topicsList}
            triggerValidation={summativeTestForm.trigger}
            watchedValues={watchedValues}
          />
        )}

        <div className="col-span-full flex flex-col">
          <GenerateButton
            isPremium
            buttonText={finalButtonText}
            creditCost={defaultPremiumCreditCost}
            disabled={isGenerateButtonDisabled}
            onClick={customFormValidator}
          />
        </div>
      </>
    </MainContent>
  );
}

SummativeTest.getLayout = function getLayout(page: ReactElement) {
  return <SidebarLayout>{page}</SidebarLayout>;
};
